import { OrgPaymentTier, WorkflowType } from '@kindo/universal';
import { useState } from 'react';

import { cn } from '../../../utils/tailwind.utils';
import {
  Button,
  ButtonType,
  Icon,
  Icons,
  Size,
  TextIconColor,
  Typography,
  TypographyCasing,
  TypographyFont,
  TypographySize,
  TypographyWeight
} from '../../core';
import { HugeIcons } from '../../core/HugeIcon';
import { TypographyWrap } from '../../core/Typography';
import { darkGlowWorkflowCardContainerStyles } from '../../WorkflowNavScreen/WorkflowNav.styles';

import {
  getFeatureStrListByWorkflowType,
  WORKFLOW_FEATURE_TO_DISPLAY_NAME,
  WORKFLOW_TYPE_TO_DESCRIPTION,
  WORKFLOW_TYPE_TO_ICON,
  WORKFLOW_TYPE_TO_USE_CASES
} from './WorkflowCatalogue.consts';
import {
  cardContainerStyles,
  expandableContentStyles,
  learnMoreButtonStyles
} from './WorkflowCatalogueCard.styles';

import { WORKFLOW_TYPE_TO_LABEL } from '~/constants/workflow.consts';
import { useAppSelector } from '~/hooks';

interface WorkflowCatalogueCardProps {
  onBuildClick: (workflowType: WorkflowType) => void;
  workflowType: WorkflowType;
}

const WorkflowCatalogueCard: React.FC<WorkflowCatalogueCardProps> = ({
  workflowType,
  onBuildClick
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { supported, unsupported } =
    getFeatureStrListByWorkflowType(workflowType);
  const { org } = useAppSelector((state) => state.user);
  const isBuildWorkflowDisabled =
    org?.paymentTier === OrgPaymentTier.FREE &&
    workflowType === WorkflowType.TRIGGERED;

  return (
    <div
      className={cn(
        darkGlowWorkflowCardContainerStyles({
          useOnHoverOnly: true,
          isActive: isExpanded
        }),
        cardContainerStyles({ isExpanded })
      )}
      data-testid="workflow-catalogue-card-container"
    >
      <div
        className="flex w-full flex-row gap-6"
        data-testid="workflow-catalogue-main-content"
      >
        <HugeIcons
          icon={WORKFLOW_TYPE_TO_ICON[workflowType]}
          size={Size.LARGE}
        />
        <div
          className="flex w-full items-center justify-between"
          data-testid="workflow-catalogue-text-button-container"
        >
          <div
            className="flex flex-col gap-2"
            data-testid="workflow-catalogue-text-container"
          >
            <Typography
              casing={TypographyCasing.CAPITALIZE}
              color={TextIconColor.PRIMARY}
              font={TypographyFont.HEADING}
              size={TypographySize.H3}
              weight={TypographyWeight.NORMAL}
            >
              {WORKFLOW_TYPE_TO_LABEL[workflowType]} Agent
            </Typography>
            <Typography
              casing={TypographyCasing.NONE}
              color={TextIconColor.SECONDARY}
              font={TypographyFont.PARAGRAPH}
              size={TypographySize.MEDIUM}
              weight={TypographyWeight.NORMAL}
            >
              {WORKFLOW_TYPE_TO_DESCRIPTION[workflowType]}
            </Typography>
            <div
              className={learnMoreButtonStyles({ isExpanded })}
              data-testid="workflow-catalogue-learn-more-button"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <Typography
                casing={TypographyCasing.UPPERCASE}
                color={TextIconColor.PRIMARY}
                size={TypographySize.X_SMALL}
                weight={TypographyWeight.SEMI_LIGHT}
              >
                Learn more
              </Typography>
              <Icons
                color={undefined}
                icon={Icon.DIRECTION_DOWN}
                invert={isExpanded}
                size={Size.MEDIUM}
              />
            </div>
          </div>
          <Button
            disabled={isBuildWorkflowDisabled}
            endIcon={Icon.BOLT}
            label="Build Agent"
            onClick={() => onBuildClick(workflowType)}
            tooltip={
              isBuildWorkflowDisabled
                ? 'Contact sales to upgrade and build triggered agents'
                : undefined
            }
            type={ButtonType.OUTLINED}
          />
        </div>
      </div>
      <div
        className={expandableContentStyles({ isExpanded })}
        data-testid="workflow-catalogue-expandable-content"
      >
        <div
          className="flex p-2 pt-4"
          data-testid="workflow-catalogue-additional-specs"
        >
          <div
            className="flex w-2/3 flex-col gap-1 divide-y divide-white/10"
            data-testid="workflow-catalogue-possible-uses"
          >
            <Typography
              casing={TypographyCasing.UPPERCASE}
              font={TypographyFont.HEADING}
              size={TypographySize.X_SMALL}
            >
              Possible Uses
            </Typography>
            <div className="flex flex-col gap-1 py-2 pt-4">
              {WORKFLOW_TYPE_TO_USE_CASES[workflowType].map((useCase) => (
                <Typography
                  color={TextIconColor.SECONDARY}
                  key={useCase.title}
                  size={TypographySize.SMALL}
                  weight={TypographyWeight.BOLD}
                >
                  {useCase.title}
                  <Typography
                    color={TextIconColor.SECONDARY}
                    key={useCase.description}
                    size={TypographySize.SMALL}
                  >
                    {useCase.description}
                  </Typography>
                </Typography>
              ))}
            </div>
          </div>
          <div
            className="flex w-1/3 flex-col gap-1 divide-y divide-white/10"
            data-testid="workflow-catalogue-attributes"
          >
            <div className="flex items-center gap-2 pl-8">
              <Typography
                casing={TypographyCasing.UPPERCASE}
                font={TypographyFont.HEADING}
                size={TypographySize.X_SMALL}
                wrap={TypographyWrap.NORMAL}
              >
                Attributes
              </Typography>
            </div>
            <div className="flex flex-col gap-1 py-2 pt-4">
              {supported.map((feature) => (
                <div className="flex items-center gap-2 pl-8" key={feature}>
                  <Icons
                    color={TextIconColor.PRIMARY}
                    icon={Icon.PLUS}
                    size={Size.SMALL}
                  />
                  <Typography
                    casing={TypographyCasing.CAPITALIZE}
                    color={TextIconColor.PRIMARY}
                    key={feature}
                    size={TypographySize.SMALL}
                  >
                    {WORKFLOW_FEATURE_TO_DISPLAY_NAME[feature]}
                  </Typography>
                </div>
              ))}
              {unsupported.map((feature) => (
                <div className="flex items-center gap-2 pl-8" key={feature}>
                  <Icons
                    color={TextIconColor.SECONDARY}
                    icon={Icon.MINUS}
                    size={Size.SMALL}
                  />
                  <Typography
                    casing={TypographyCasing.CAPITALIZE}
                    color={TextIconColor.SECONDARY}
                    key={feature}
                    size={TypographySize.X_SMALL}
                  >
                    {WORKFLOW_FEATURE_TO_DISPLAY_NAME[feature]}
                  </Typography>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowCatalogueCard;
